/**
 * generateEntraAppsDocs.ts - AutoDoc "Applications Entra ID".
 *   - downloadEntraAppsListingDocx  : 1 document listing (toutes les apps)
 *   - downloadEntraAppDocx          : 1 document pour UNE application
 *   - downloadAllEntraAppDocs       : génère 1 document par application (séquentiel)
 */
import { Document, Packer, Paragraph, Table, TableRow, WidthType, HeadingLevel, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import {
  AUTODOC_STYLES, buildCover, buildIntro, cell, headerCell, safeName, fmtDate,
} from './autodocCommon';
import type { AutodocMeta } from './generateAutodocReports';
import type { EntraApp, EntraAppsFacts } from '../api/entraAppsApi';

function para(text: string, size = 20): Paragraph {
  return new Paragraph({ children: [new TextRun({ text, size })] });
}
function h1(text: string): Paragraph {
  return new Paragraph({ text, heading: HeadingLevel.HEADING_1, pageBreakBefore: true });
}
function credColor(status?: string): string {
  const v = (status ?? '').toLowerCase();
  if (v === 'expire') return 'B82329';
  if (v === 'expire bientot') return 'A86317';
  if (v === 'valide') return '1F7A45';
  return '7A7A7A';
}
function kvTable(rows: [string, string][]): Table {
  return new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: rows.map(([k, v]) => new TableRow({ children: [headerCell(k, 30), cell(v || '—')] })) });
}

// ===========================================================================
//  1 document pour UNE application
// ===========================================================================
function appChapters(app: EntraApp): (Paragraph | Table)[] {
  const out: (Paragraph | Table)[] = [];

  // Identité
  out.push(h1('Identité de l’application'));
  out.push(kvTable([
    ['Nom', app.displayName],
    ['Application (client) ID', app.appId],
    ['Object ID', app.objectId],
    ['Créé le', fmtDate(app.createdDateTime) || '—'],
    ['Type de comptes (audience)', app.signInAudience ?? '—'],
    ['Domaine éditeur', app.publisherDomain ?? '—'],
    ['Notes', app.notes ?? '—'],
  ]));
  out.push(new Paragraph({ children: [] }));

  // Propriétaires
  out.push(h1('Propriétaires'));
  out.push(app.owners.length > 0 ? para(app.owners.join(', ')) : para('Aucun propriétaire déclaré.'));
  out.push(new Paragraph({ children: [] }));

  // URIs
  out.push(h1('URIs'));
  out.push(para('Identifier URIs :', 18));
  out.push(app.identifierUris.length > 0 ? para(app.identifierUris.join('  |  ')) : para('—'));
  out.push(new Paragraph({ children: [] }));
  out.push(para('Redirect URIs :', 18));
  out.push(app.redirectUris.length > 0 ? para(app.redirectUris.join('  |  ')) : para('—'));
  out.push(new Paragraph({ children: [] }));

  // Credentials
  out.push(h1('Secrets & certificats'));
  if (app.credentials.length > 0) {
    out.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: [
      new TableRow({ tableHeader: true, children: [headerCell('Type', 16), headerCell('Nom', 30), headerCell('Expiration', 22), headerCell('Jours', 12), headerCell('État', 20)] }),
      ...app.credentials.map((c) => new TableRow({ children: [
        cell(c.type),
        cell(c.name || '—'),
        cell(fmtDate(c.endDateTime) || '—'),
        cell(c.daysToExpiry == null ? '—' : String(c.daysToExpiry)),
        cell(c.status || '—', { bold: true, color: credColor(c.status) }),
      ] })),
    ] }));
  } else {
    out.push(para('Aucun secret ni certificat.'));
  }
  out.push(new Paragraph({ children: [] }));

  // Permissions API
  out.push(h1('Permissions API demandées'));
  if (app.permissions.length > 0) {
    out.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: [
      new TableRow({ tableHeader: true, children: [headerCell('API', 34), headerCell('Type', 20), headerCell('Permission', 46)] }),
      ...app.permissions.map((p) => new TableRow({ children: [
        cell(p.api),
        cell(p.type, { bold: p.type === 'Application', color: p.type === 'Application' ? 'B82329' : '4A4A6E' }),
        cell(p.permission),
      ] })),
    ] }));
    out.push(para('Note : les permissions de type « Application » s’exécutent sans utilisateur — à revoir en priorité.', 16));
  } else {
    out.push(para('Aucune permission API demandée.'));
  }
  out.push(new Paragraph({ children: [] }));

  // Exposition
  out.push(h1('Exposition (API de l’application)'));
  out.push(para(`Rôles d’application exposés : ${app.exposedRoles} · Scopes délégués exposés : ${app.exposedScopes}.`));

  return out;
}

export async function downloadEntraAppDocx(params: { meta: AutodocMeta; app: EntraApp }): Promise<void> {
  const { meta, app } = params;
  const cover = await buildCover({
    brandLine: 'AutoDoc',
    title:     app.displayName || 'Application Entra ID',
    subtitle:  'Application Entra ID — configuration',
    orgName:   meta.orgName,
    logo:      meta.logo,
    boxLines:  [['Application ID', app.appId]],
    footnote:  'Configuration extraite en direct via Microsoft Graph.',
  });
  const intro = buildIntro({
    purpose: `Documenter la configuration de l’application Entra ID « ${app.displayName} » : identité, propriétaires, URIs, secrets/certificats, permissions API et exposition.`,
    audience: 'RSSI, équipe IAM, auditeur.',
    scope: meta.tenantDomain ? `Tenant ${meta.tenantDomain}.` : 'Tenant Microsoft 365 connecté.',
  });
  const doc = new Document({
    creator: 'NIS365 Auditor', title: `App Entra - ${app.displayName}`, styles: AUTODOC_STYLES,
    sections: [{ children: [...cover, ...intro, ...appChapters(app)] }],
  });
  saveAs(await Packer.toBlob(doc), `App_Entra_${safeName(app.displayName || app.appId)}_${new Date().toISOString().slice(0, 10)}.docx`);
}

// ===========================================================================
//  1 document LISTING (toutes les apps)
// ===========================================================================
export async function downloadEntraAppsListingDocx(params: { meta: AutodocMeta; facts: EntraAppsFacts }): Promise<void> {
  const { meta, facts } = params;
  const s = facts.summary;
  const cover = await buildCover({
    brandLine: 'AutoDoc',
    title:     'Applications Entra ID',
    subtitle:  'Listing des enregistrements d’applications (App Registrations)',
    orgName:   meta.orgName,
    logo:      meta.logo,
    boxLines:  [['Applications', String(s?.total ?? facts.apps.length)]],
    footnote:  'Inventaire extrait en direct via Microsoft Graph.',
  });
  const intro = buildIntro({
    purpose: 'Inventorier les App Registrations du tenant : audience, credentials (et expirations), volume de permissions API, propriétaires.',
    audience: 'RSSI, équipe IAM, auditeur.',
    scope: meta.tenantDomain ? `Tenant ${meta.tenantDomain}.` : 'Tenant Microsoft 365 connecté.',
  });

  const body: (Paragraph | Table)[] = [
    h1('Synthèse'),
    para(s ? `${s.total} application(s) · ${s.withExpiredCreds} avec credential(s) expiré(s) (${s.totalExpired} au total) · ${s.withSecrets} avec secret · ${s.withCerts} avec certificat.` : `${facts.apps.length} application(s).`, 18),
    new Paragraph({ children: [] }),
    h1('Inventaire des applications'),
    new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: [
      new TableRow({ tableHeader: true, children: [
        headerCell('Application', 30), headerCell('App ID', 22), headerCell('Audience', 14),
        headerCell('Secr./Cert.', 10), headerCell('Exp.', 8), headerCell('Perm.', 8), headerCell('Propriétaires', 18),
      ] }),
      ...facts.apps.map((a) => new TableRow({ children: [
        cell(a.displayName, { bold: true }),
        cell(a.appId),
        cell(a.signInAudience ?? '—'),
        cell(`${a.secretsCount}/${a.certsCount}`),
        cell(String(a.expiredCreds), { bold: a.expiredCreds > 0, color: a.expiredCreds > 0 ? 'B82329' : '7A7A7A' }),
        cell(String(a.permissions.length)),
        cell(a.owners.join(', ') || '—'),
      ] })),
    ] }),
  ];

  const doc = new Document({
    creator: 'NIS365 Auditor', title: `Listing Apps Entra - ${meta.orgName}`, features: { updateFields: true }, styles: AUTODOC_STYLES,
    sections: [{ children: [...cover, ...intro, ...body] }],
  });
  saveAs(await Packer.toBlob(doc), `Applications_Entra_ID_${safeName(meta.orgName)}_${new Date().toISOString().slice(0, 10)}.docx`);
}

// ===========================================================================
//  1 document PAR application (téléchargements séquentiels)
// ===========================================================================
export async function downloadAllEntraAppDocs(params: { meta: AutodocMeta; facts: EntraAppsFacts }): Promise<number> {
  const { meta, facts } = params;
  let n = 0;
  for (const app of facts.apps) {
    await downloadEntraAppDocx({ meta, app });
    n++;
    // Laisse le navigateur enchaîner les téléchargements successifs.
    await new Promise((r) => setTimeout(r, 500));
  }
  return n;
}
